import {
  fetchEditColumnsData,
  fetchTable,
  fetchSellers,
  fetchStores,
  fetchCampaigns,
  fetchClusters,
  fetchIndicators,
  postEditColumnsTable,
  fetchTotalizers,
} from '@/features/EngagementReport/data';
import { SelectedFilter, SelectedPreset } from '@/features/EngagementReport/models';
import { getExportJobDetails } from '@/features/EngagementReport/services';
import { cancelExportImportJob } from '@/services/audiences';
import isEmpty from 'lodash/isEmpty';

const engagementReport = {
  namespaced: true,
  state: {
    // filters
    segments: [
      { id: 'all', name: 'all' },
      { id: 'store', name: '_store' },
      { id: 'seller', name: '_seller' },
      { id: 'campaign', name: '_campaign' },
      { id: 'cluster', name: '_cluster' },
    ],

    campaignId: '',
    sellerId: '',
    storeId: '',
    cluster: '',
    selectedSegment: '',
    stores: [],
    sellers: [],
    campaigns: [],
    clusters: [],
    filterSelected: [],
    defaultList: [],
    isLoadingAllFilters: {
      isLoadingStores: false,
      isLoadingSellers: false,
      isLoadingCampaigns: false,
      isLoadingClusters: false,
    },
    hasSomeFilterApplied: false,

    // expand table
    isTableExpanded: false,

    // table
    table: {
      header: [],
      body: [],
      summaryRow: [],
    },
    pagination: {
      page: 1,
      limit: 10,
      count: 0,
    },
    limitRange: [10, 25, 50, 100],
    orderBy: '',
    isLoading: false,
    hasError: false,
    isEditColumnsVisible: false,
    isEmpty: false,
    query: [],

    // edit table metrics
    metricsAndDimensions: [],
    metricsAndDimensionsOrdering: [],
    checkedMetricsAndDimensions: [],
    selectedMetricsAndDimensions: [],
    defaultSelectedMetricsAndDimensions: [],
    lastCustomConfig: {},
    preset: 'STORE',
    metric: {
      key: 'STORE',
      name: 'Loja',
    },
    isEditColumnsLoading: false,
    fixedColumns: [],

    // export table
    batchId: '',
    progress: 0,
    urlFile: '',
    isModalProgressVisible: false,
    isModalExportSuccessVisible: false,
    isModalExportErrorVisible: false,

    // kpis / graphic
    kpis: {},
    isIndicatorsLoading: false,
    hasIndicatorsError: false,
    graphic: {},
    hasDataOnGraphic: false,
  },

  mutations: {
    SET_SELECTED_SEGEMENT(state, segement) {
      state.selectedSegment = segement;
    },
    SET_SELLERS(state, sellersList) {
      state.sellers = sellersList;
    },
    SET_STORES(state, storesList) {
      state.stores = storesList;
    },
    SET_CAMPAIGNS(state, campaignsList) {
      state.campaigns = campaignsList;
    },
    SET_CLUSTERS(state, clustersList) {
      state.clusters = clustersList;
    },
    SET_TABLE(state, tableData) {
      state.table.header = tableData.header;
      state.table.body = tableData.body;
      state.table.summaryRow = tableData.summaryRow;
    },
    SET_IS_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_IS_EDIT_COLUMNS_LOADING(state, isLoading) {
      state.isEditColumnsLoading = isLoading;
    },
    SET_HAS_ERROR(state, hasError) {
      state.hasError = hasError;
    },
    SET_EMPTY_TABLE(state, isEmpty) {
      state.isEmpty = isEmpty;
    },
    SET_PRESET(state, preset) {
      state.preset = preset;
    },
    SET_METRIC(state, metric) {
      state.metric = metric;
    },
    SET_QUERY(state, query) {
      const { column } = query;
      const filterByColumn = state.query.map(query => query.column);

      if (filterByColumn.includes(column)) {
        const filteredQuery = state.query.filter(query => query.column !== column);
        state.query = [...filteredQuery, query];
      } else {
        state.query = [...state.query, query];
      }
    },
    CLEAR_QUERY_SEARCH(state, column) {
      if (!column) return (state.query = []);
      return (state.query = state.query.filter(query => query.column !== column));
    },
    // edit table metrics
    SET_METRICS_AND_DIMENSIONS(state, metricsAndDimensions) {
      state.metricsAndDimensions = metricsAndDimensions;
    },
    SET_METRICS_AND_DIMENSIONS_ORDERING(state, metricsAndDimensionsOrdering) {
      state.metricsAndDimensionsOrdering = metricsAndDimensionsOrdering;
    },
    SET_SELECTED_METRICS_AND_DIMENSIONS(state, metricsAndDimensions) {
      state.selectedMetricsAndDimensions = metricsAndDimensions;
      state.defaultSelectedMetricsAndDimensions = metricsAndDimensions;
    },
    SET_IS_EDIT_COLUMNS_VISIBLE(state, isVisible) {
      state.isEditColumnsVisible = isVisible;
    },
    SET_CHECKED_METRICS_AND_DIMENSIONS(state, checked) {
      state.checkedMetricsAndDimensions = checked;
    },
    DELETE_CHECKED_METRIC_AND_DIMENSION(state, key) {
      state.selectedMetricsAndDimensions = state.selectedMetricsAndDimensions.filter(item => item.key !== key);
      state.metricsAndDimensionsOrdering = state.metricsAndDimensionsOrdering.filter(item => item.key !== key);
    },
    SET_UPDATE_CHECKED_SELECTED_METRICS_AND_DIMENSIONS(state, metric) {
      state.selectedMetricsAndDimensions = [...state.selectedMetricsAndDimensions, metric];
      state.metricsAndDimensionsOrdering = [...state.metricsAndDimensionsOrdering, metric];
    },
    SET_DEFAULT_SELECTED_METRICS_AND_DIMENSIONS(state) {
      state.selectedMetricsAndDimensions = state.defaultSelectedMetricsAndDimensions;
    },
    SET_ORDER_BY(state, payload) {
      state.orderBy = payload;
    },
    // expand table
    SET_IS_TABLE_EXPANDED(state, isExpanded) {
      state.isTableExpanded = isExpanded;
    },
    // filters
    SET_DEFAULT_LIST(state, defaultList) {
      state.defaultList = defaultList;
    },
    SET_FILTER_SELECTED(state, filterSelected) {
      state.filterSelected = filterSelected;
    },
    SET_FILTER_SELECTED_ID(state, selectedSegmentId) {
      const filteredById = state.filterSelected.filter(item => item.isChecked).map(item => item.value);
      const filteredByName = state.filterSelected.filter(item => item.isChecked).map(item => item.label);

      state.sellerId = selectedSegmentId === SelectedFilter.SELLER ? filteredById.join(',') : [];
      state.storeId = selectedSegmentId === SelectedFilter.STORE ? filteredById.join(',') : [];
      state.campaignId = selectedSegmentId === SelectedFilter.CAMPAIGN ? filteredById.join(',') : [];
      state.cluster = selectedSegmentId === SelectedFilter.CLUSTER ? filteredByName.join(',') : [];
    },
    SET_IS_LOADING_ALL_FILTERS(state, payload) {
      const { name } = payload;
      state.isLoadingAllFilters[name] = payload.isLoading;
    },
    SET_HAS_SOME_FILTER_APPLIED(state, boolean) {
      state.hasSomeFilterApplied = boolean;
    },
    SET_RESET_FILTER(state) {
      state.campaignId = [];
      state.sellerId = [];
      state.storeId = [];
      state.cluster = [];
      state.defaultList = [];
    },
    SET_FIXED_COLUMNS(state, columns) {
      state.fixedColumns = columns;
    },
    // export table
    SET_BATCH_ID(state, batchId) {
      state.batchId = batchId;
    },
    SET_PROGRESS(state, progress) {
      state.progress = progress;
    },
    SET_URL_FILE(state, urlFile) {
      state.urlFile = urlFile;
    },
    SET_IS_MODAL_PROGRESS_VISIBLE(state, boolean) {
      state.isModalProgressVisible = boolean;
    },
    SET_IS_MODAL_EXPORT_SUCCESS_VISIBLE(state, boolean) {
      state.isModalExportSuccessVisible = boolean;
    },
    SET_IS_MODAL_EXPORT_ERROR_VISIBLE(state, boolean) {
      state.isModalExportErrorVisible = boolean;
    },
    // kpis / graphic
    SET_KPIS(state, kpis) {
      state.kpis = kpis;
    },
    SET_IS_INDICATORS_LOADING(state, isLoading) {
      state.isIndicatorsLoading = isLoading;
    },
    SET_HAS_INDICATORS_ERROR(state, hasError) {
      state.hasIndicatorsError = hasError;
    },
    SET_GRAPHIC(state, data) {
      state.graphic = data;
    },
    SET_HAS_DATA_ON_GRAPHIC(state, hasData) {
      state.hasDataOnGraphic = hasData;
    },
  },
  actions: {
    updateDefaultList({ commit }, payload) {
      commit('SET_DEFAULT_LIST', payload);
    },
    // filters
    setSelectedSegement({ commit, state }, payload) {
      commit('SET_SELECTED_SEGEMENT', payload);

      if (payload === SelectedFilter.SELLER) return commit('SET_FILTER_SELECTED', state.sellers);
      if (payload === SelectedFilter.STORE) return commit('SET_FILTER_SELECTED', state.stores);
      if (payload === SelectedFilter.CAMPAIGN) return commit('SET_FILTER_SELECTED', state.campaigns);
      if (payload === SelectedFilter.CLUSTER) return commit('SET_FILTER_SELECTED', state.clusters);
    },
    setApplyFilters({ commit, dispatch, state }, payload) {
      if (state.filterSelected.some(item => item.isChecked)) {
        commit('SET_FILTER_SELECTED_ID', state.selectedSegment);
      } else {
        dispatch('setResetFilter');
      }
      commit('SET_HAS_SOME_FILTER_APPLIED', true);
      dispatch('updateDefaultList', payload);
      dispatch('setFetchTable');
      dispatch('setFetchIndicators');
    },
    setFetchSegmentOptions({ dispatch }, selectedSegment) {
      if (selectedSegment === 'store') dispatch('setStores');
      if (selectedSegment === 'seller') dispatch('setSellers');
      if (selectedSegment === 'campaign') dispatch('setCampaigns');
      if (selectedSegment === 'cluster') dispatch('setClusters');
    },
    setStores({ commit }) {
      commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingStores', isLoading: true });
      fetchStores()
        .then(response => {
          const data = response?.data;

          if (!data) {
            commit('SET_STORES', []);
            commit('SET_DEFAULT_LIST', []);
            return;
          }

          const formattedData = data?.map(item => {
            return { ...item, isChecked: false };
          });

          commit('SET_STORES', formattedData);
          commit('SET_DEFAULT_LIST', formattedData);
        })
        .catch(() => {
          commit('SET_STORES', []);
          commit('SET_DEFAULT_LIST', []);
        })
        .finally(() => {
          commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingStores', isLoading: false });
        });
    },
    setSellers({ commit }) {
      commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingSellers', isLoading: true });
      fetchSellers()
        .then(response => {
          if (!response) {
            commit('SET_SELLERS', []);
            commit('SET_DEFAULT_LIST', []);
            return;
          }

          const formattedData = response?.map(item => {
            return { ...item, isChecked: false };
          });

          commit('SET_SELLERS', formattedData);
          commit('SET_DEFAULT_LIST', formattedData);
        })
        .catch(() => {
          commit('SET_SELLERS', []);
          commit('SET_DEFAULT_LIST', []);
        })
        .finally(() => {
          commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingSellers', isLoading: false });
        });
    },
    setCampaigns({ commit }) {
      commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingCampaigns', isLoading: true });
      fetchCampaigns()
        .then(response => {
          if (!response) {
            commit('SET_CAMPAIGNS', []);
            commit('SET_DEFAULT_LIST', []);
            return;
          }

          const formattedData = response?.map(item => {
            return { ...item, isChecked: false };
          });

          commit('SET_CAMPAIGNS', formattedData);
          commit('SET_DEFAULT_LIST', formattedData);
        })
        .catch(() => {
          commit('SET_CAMPAIGNS', []);
          commit('SET_DEFAULT_LIST', []);
        })
        .finally(() => {
          commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingCampaigns', isLoading: false });
        });
    },
    setClusters({ commit }) {
      commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingClusters', isLoading: true });
      fetchClusters()
        .then(response => {
          if (!response) {
            commit('SET_CLUSTERS', []);
            commit('SET_DEFAULT_LIST', []);
            return;
          }

          const formattedData = response?.map(item => {
            return { ...item, isChecked: false };
          });

          commit('SET_CLUSTERS', formattedData);
          commit('SET_DEFAULT_LIST', formattedData);
        })
        .catch(() => {
          commit('SET_CLUSTERS', []);
          commit('SET_DEFAULT_LIST', []);
        })
        .finally(() => {
          commit('SET_IS_LOADING_ALL_FILTERS', { name: 'isLoadingClusters', isLoading: false });
        });
    },
    setUpdateCheckedStatus({ commit }, payload) {
      commit('SET_FILTER_SELECTED', payload);
    },
    setResetFilter({ commit }) {
      commit('SET_RESET_FILTER');
    },
    setResetFilterIds({ state, dispatch }) {
      dispatch('setResetFilter');
      dispatch('setFetchSegmentOptions', state.selectedSegment);
    },
    // table
    setFetchTable({ commit, state, dispatch }) {
      const preset = state.preset;
      const limit = state.pagination.limit;
      const page = state.pagination.page;
      const orderBy = state.orderBy;
      const campaignId = state.campaignId;
      const sellerId = state.sellerId;
      const storeId = state.storeId;
      const cluster = state.cluster;
      const query = state.query;

      commit('SET_IS_LOADING', true);
      commit('SET_HAS_ERROR', false);
      commit('SET_EMPTY_TABLE', false);

      fetchTable({ preset, limit, page, orderBy, query, campaignId, storeId, sellerId, cluster })
        .then(response => {
          if (response?.data.length === 0) {
            commit('SET_EMPTY_TABLE', true);
          }
          const tableData = {
            header: [],
            body: [],
            summaryRow: [],
          };

          tableData.header = response?.series;
          tableData.body = response?.data;
          tableData.summaryRow = response?.dataCalcTotal;
          state.pagination.count = response?.totalCount;

          commit('SET_TABLE', tableData);
          dispatch('setFetchTableTotalizers');
        })
        .catch(() => {
          commit('SET_HAS_ERROR', true);
        })
        .finally(() => {
          commit('SET_IS_LOADING', false);
        });
    },
    setFetchTableTotalizers({ commit, state }) {
      const preset = state.preset;
      const limit = state.pagination.limit;
      const page = state.pagination.page;
      const orderBy = state.orderBy;
      const campaignId = state.campaignId;
      const sellerId = state.sellerId;
      const storeId = state.storeId;
      const cluster = state.cluster;
      const query = state.query;

      commit('SET_IS_LOADING', true);
      commit('SET_HAS_ERROR', false);

      fetchTotalizers({ preset, limit, page, orderBy, query, campaignId, storeId, sellerId, cluster })
        .then(response => {
          commit('SET_TABLE', { ...state.table, summaryRow: response?.data });
        })
        .catch(() => {
          commit('SET_HAS_ERROR', true);
        })
        .finally(() => {
          commit('SET_IS_LOADING', false);
        });
    },
    updateDefaultPreset({ commit }, preset) {
      commit('SET_PRESET', preset);
    },
    setFixedColumns({ commit }, columns) {
      commit('SET_FIXED_COLUMNS', columns);
    },
    setPreset({ commit, dispatch }, preset) {
      commit('SET_PRESET', preset);

      dispatch('setClearSearch');
      dispatch('updateCheckedMetricsAndDimensions', { key: [] });
      dispatch('setOrderBy', '');
      dispatch('updateSelectedMetricsAndDimensions', []);
      dispatch('setFetchTable');
    },
    setMetric({ commit }, metric) {
      commit('SET_METRIC', metric);
    },
    // edit table metrics
    setEditColumnsIsVisible({ commit }, payload) {
      commit('SET_IS_EDIT_COLUMNS_VISIBLE', payload);
    },
    loadMetricsAndDimensions({ commit, dispatch, state }, preset) {
      commit('SET_IS_EDIT_COLUMNS_LOADING', true);

      fetchEditColumnsData(preset)
        .then(response => {
          const metricsAndDimensions = response?.tableMetrics;
          const ordering = response?.ordering;

          const dimensions = metricsAndDimensions.find(group => group.key === 'Dimensões')?.data || [];

          dispatch('setFixedColumns', response.fixedColumns);

          if (state.preset !== SelectedPreset.DATE) {
            dimensions
              .filter(
                dimension =>
                  state.fixedColumns[state.preset].includes(dimension.key) &&
                  !ordering.some(item => item.key === dimension.key),
              )
              .forEach(dimension => ordering.push(dimension));
          }

          if (state.preset === SelectedPreset.DATE) {
            const orderingKeys = ordering.map(item => item.key);
            const hasDimensionKey = state.fixedColumns[state.preset].some(dimensionKey =>
              orderingKeys.includes(dimensionKey),
            );

            if (!hasDimensionKey) {
              const suggestionDate = dimensions.find(item => item.key === 'SUGGESTION_DATE');
              if (suggestionDate) ordering.unshift(suggestionDate);
            }
          }

          commit('SET_METRICS_AND_DIMENSIONS', metricsAndDimensions);
          commit('SET_METRICS_AND_DIMENSIONS_ORDERING', ordering);
          dispatch('setCheckedDefaultMetricsAndDimensions', ordering);
        })
        .finally(() => {
          commit('SET_IS_EDIT_COLUMNS_LOADING', false);
        });
    },
    updateSelectedMetricsAndDimensions({ commit }, payload) {
      commit('SET_SELECTED_METRICS_AND_DIMENSIONS', payload);
    },
    setCheckedDefaultMetricsAndDimensions({ state, dispatch }, data) {
      state.defaultSelectedMetricsAndDimensions = data;

      dispatch('updateSelectedMetricsAndDimensions', data);
    },
    updateCheckedMetricsAndDimensions({ state, commit, dispatch }, payload) {
      const { key, type } = payload;

      if (!key.length) {
        return;
      }
      commit('SET_CHECKED_METRICS_AND_DIMENSIONS', key);

      if (type === 'draggable') return;

      commit('SET_ORDER_BY', '');
      commit('SET_IS_LOADING', true);

      const metricsAndDimensions = key.join(',');

      postEditColumnsTable(metricsAndDimensions, state.preset, 'engagement').then(response => {
        if (response) {
          dispatch('setFetchTable');
        }
      });
    },
    deleteCheckedMetricsAndDimensions({ commit }, payload) {
      commit('DELETE_CHECKED_METRIC_AND_DIMENSION', payload);
    },
    setUpdateCheckedSelectedMetricsAndDimensions({ commit }, payload) {
      commit('SET_UPDATE_CHECKED_SELECTED_METRICS_AND_DIMENSIONS', payload);
    },
    setResetToDefaultMetricsAndDimensions({ commit, dispatch }) {
      commit('SET_DEFAULT_SELECTED_METRICS_AND_DIMENSIONS');
      dispatch('updateCheckedMetricsAndDimensions', { key: [''] });
    },
    setOrderBy({ commit }, payload) {
      commit('SET_ORDER_BY', payload);
    },
    setQuery({ commit }, payload) {
      commit('SET_QUERY', payload);
    },
    setClearSearch({ commit }, payload) {
      commit('CLEAR_QUERY_SEARCH', payload);
    },
    // expand table
    setIsTableExpanded({ commit }, isExpanded) {
      commit('SET_IS_TABLE_EXPANDED', isExpanded);
    },
    updateTableByDatepicker({ dispatch }) {
      dispatch('setFetchTable');
    },
    // export table
    setBatchId({ commit }, batchId) {
      commit('SET_BATCH_ID', batchId);
    },
    setIsModalProgressVisible({ commit }, boolean) {
      commit('SET_IS_MODAL_PROGRESS_VISIBLE', boolean);
    },
    setIsModalExportSuccessVisible({ commit }, boolean) {
      commit('SET_IS_MODAL_EXPORT_SUCCESS_VISIBLE', boolean);
    },
    setIsModalExportErrorVisible({ commit }, boolean) {
      commit('SET_IS_MODAL_EXPORT_ERROR_VISIBLE', boolean);
    },
    setCancelExportJobDetails({ state }) {
      if (state.batchId) cancelExportImportJob(state.batchId).then();
    },
    setReset({ commit }) {
      commit('SET_BATCH_ID', '');
      commit('SET_URL_FILE', '');
      commit('SET_PROGRESS', 0);
    },
    setExportJobDetails({ commit, state, dispatch }) {
      dispatch('setIsModalExportSuccessVisible', false);
      const batchId = state.batchId;

      if (!batchId) return;

      getExportJobDetails(batchId).then(({ data }) => {
        const errorCodes = ['job-cancelled-with-errors', 'clickhouse-select-fail'];
        const response = data?.data;
        const progress = response?.result?.progress;
        const urlFile = response?.result?.url;
        const cancelledByUser = response?.result?.code === 'job-cancelled-by-user';
        const cancelledWithErrors = errorCodes.includes(response?.result?.code);

        commit('SET_PROGRESS', progress);

        if (cancelledByUser) {
          commit('SET_PROGRESS', 0);
          dispatch('setIsModalProgressVisible', false);
        } else if (cancelledWithErrors) {
          commit('SET_PROGRESS', 0);
          dispatch('setIsModalProgressVisible', false);
          setTimeout(() => {
            dispatch('setIsModalExportErrorVisible', true);
          }, 1000);
        } else if (!urlFile) {
          setTimeout(() => {
            dispatch('setExportJobDetails');
          }, 3000);
        } else {
          commit('SET_URL_FILE', urlFile);
          setTimeout(() => {
            dispatch('setIsModalExportSuccessVisible', true);
            dispatch('setReset');
          }, 2000);
        }
      });
    },
    // kpis / graphic
    setFetchIndicators({ commit, state }) {
      commit('SET_IS_INDICATORS_LOADING', true);
      commit('SET_HAS_INDICATORS_ERROR', false);
      commit('SET_HAS_DATA_ON_GRAPHIC', true);

      const campaignId = state.campaignId;
      const sellerId = state.sellerId;
      const storeId = state.storeId;
      const cluster = state.cluster;

      fetchIndicators({ campaignId, storeId, sellerId, cluster })
        .then(response => {
          if (
            isEmpty(response?.grafics?.metrics?.suggestionsUsed) ||
            isEmpty(response?.grafics?.metrics?.percentageSuggestionsUsed) ||
            isEmpty(response?.grafics?.metrics?.suggestionsAvailable)
          ) {
            commit('SET_HAS_DATA_ON_GRAPHIC', false);
          }
          commit('SET_KPIS', response?.kpis);
          commit('SET_GRAPHIC', response?.grafics);
        })
        .catch(() => {
          commit('SET_HAS_INDICATORS_ERROR', true);
          commit('SET_HAS_DATA_ON_GRAPHIC', false);
        })
        .finally(() => {
          commit('SET_IS_INDICATORS_LOADING', false);
        });
    },
  },

  getters: {
    getSelectedSegment: state => state.selectedSegment,
    getSegments: state => state.segments,
    getSellers: state => state.sellers,
    getTableHeader: state => state.table.header,
    getTableBody: state => state.table.body,
    getTableSummaryRow: state => state.table.summaryRow,
    getIsTableLoading: state => state.isLoading,
    getIsEditColumnsLoading: state => state.isEditColumnsLoading,
    getHasError: state => state.hasError,
    getIsEmpty: state => state.isEmpty,
    getIsEditColumnsVisible: state => state.isEditColumnsVisible,
    getListOfMetricsAndDimensions: state => state.metricsAndDimensions,
    getSelectedMetricsAndDimensions: state => state.selectedMetricsAndDimensions,
    getMetricsAndDimensionsOrdering: state => state.metricsAndDimensionsOrdering,
    getCheckedMetricsAndDimensions: state => state.checkedMetricsAndDimensions,
    getIsTableExpanded: state => state.isTableExpanded,
    getPreset: state => state.preset,
    getDefaultMetricsLength: state => state.defaultSelectedMetricsAndDimensions.length,
    getPagination: state => state.pagination,
    getLimitRange: state => state.limitRange,
    getFilterSelected: state => state.filterSelected,
    getDefaultList: state => state.defaultList,
    getIsLoadingAllFilters: state => state.isLoadingAllFilters,
    getHasSomeFilterApplied: state => state.hasSomeFilterApplied,
    getBatchId: state => state.batchId,
    getIsModalProgressVisible: state => state.isModalProgressVisible,
    getProgress: state => state.progress,
    getIsModalExportSuccessVisible: state => state.isModalExportSuccessVisible,
    getIsModalExportErrorVisible: state => state.isModalExportErrorVisible,
    getUrlFile: state => state.urlFile,
    getOrderBy: state => state.orderBy,
    getCampaignsId: state => state.campaignId,
    getSellersId: state => state.sellerId,
    getClusters: state => state.cluster,
    getStoresId: state => state.storeId,
    getKpis: state => state.kpis,
    getIsIndicatorsLoading: state => state.isIndicatorsLoading,
    getHasIndicatorsError: state => state.hasIndicatorsError,
    getGraphic: state => state.graphic,
    setHasDataOnGraphic: state => state.hasDataOnGraphic,
    getMetric: state => state.metric,
    getFixedColumns: state => state.fixedColumns,
  },
};

export default engagementReport;
